import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Calendar, Clock, MapPin, Wrench, Shield, FileText, Truck,
  Bell, CheckCircle, AlertTriangle, ChevronRight, LogOut, Loader2, History,
  CircleDot, Package, ShoppingCart, Pause, Search, Plus, User, Phone, X,
  Droplets, Zap, CircleSlash, Sparkles, Settings
} from "lucide-react";

const TYPE_CONFIG = {
  rar: { label: "RAR / ITP", icon: Shield, color: "bg-red-500", textColor: "text-red-400" },
  revizie: { label: "Revizie", icon: Wrench, color: "bg-blue-500", textColor: "text-blue-400" },
  reparatie: { label: "Reparatie", icon: FileText, color: "bg-orange-500", textColor: "text-orange-400" },
  livrare: { label: "Livrare", icon: Truck, color: "bg-green-500", textColor: "text-green-400" },
};

const SCOOTER_STATUS = [
  { value: "in_stock", label: "Disponibil", icon: CheckCircle, color: "border-green-500 text-green-400 hover:bg-green-500/10", active: "bg-green-500 text-white" },
  { value: "in_service", label: "In lucru", icon: Wrench, color: "border-orange-500 text-orange-400 hover:bg-orange-500/10", active: "bg-orange-500 text-white" },
  { value: "sold", label: "Vandut", icon: ShoppingCart, color: "border-red-500 text-red-400 hover:bg-red-500/10", active: "bg-red-500 text-white" },
  { value: "reserved", label: "Rezervat", icon: Pause, color: "border-blue-500 text-blue-400 hover:bg-blue-500/10", active: "bg-blue-500 text-white" },
];

const SERVICE_TYPES = [
  { value: "reparatie", label: "Reparatie", icon: Wrench },
  { value: "revizie", label: "Revizie", icon: Settings },
  { value: "schimb_ulei", label: "Schimb ulei", icon: Droplets },
  { value: "schimb_anvelope", label: "Schimb anvelope", icon: CircleDot },
  { value: "diagnostic", label: "Diagnostic", icon: Search },
  { value: "electric", label: "Electric", icon: Zap },
  { value: "franare", label: "Franare", icon: CircleSlash },
  { value: "carene", label: "Carene / Estetica", icon: Sparkles },
  { value: "altul", label: "Altul", icon: FileText },
];

const SERVICE_STATUS = {
  in_asteptare: { label: "In asteptare", color: "bg-yellow-500 text-black" },
  in_lucru: { label: "In lucru", color: "bg-blue-500 text-white" },
  finalizat: { label: "Finalizat", color: "bg-green-500 text-white" },
  anulat: { label: "Anulat", color: "bg-zinc-500 text-white" },
};

function getDaysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(dateStr); target.setHours(0,0,0,0);
  return Math.ceil((target - today) / 86400000);
}

export default function MechanicDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [programari, setProgramari] = useState([]);
  const [history, setHistory] = useState([]);
  const [scooterStatuses, setScooterStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("programat");
  const [activeTab, setActiveTab] = useState("programari");

  // Search tab state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Service passages state
  const [passages, setPassages] = useState([]);
  const [passageFilter, setPassageFilter] = useState("in_asteptare");

  useEffect(() => {
    if (activeTab === "programari") fetchProgramari();
    else if (activeTab === "istoric") fetchHistory();
    else if (activeTab === "service") fetchPassages();
  }, [filter, activeTab, passageFilter]);

  useEffect(() => {
    api.get("/mechanic-history").then(r => setHistory(r.data)).catch(() => {});
    api.get("/service-passages?status=in_asteptare").then(r => setPassages(r.data)).catch(() => {});
  }, []);

  const fetchProgramari = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/programari?status=${filter}`);
      setProgramari(resp.data);
      const ids = [...new Set(resp.data.map(p => p.scooter_id).filter(Boolean))];
      const statuses = {};
      for (const id of ids) {
        try { const sr = await api.get(`/scooters/${id}`); statuses[id] = sr.data?.status || "in_stock"; } catch (e) { statuses[id] = "?"; }
      }
      setScooterStatuses(statuses);
    } catch (err) { toast.error("Eroare"); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try { const resp = await api.get("/mechanic-history"); setHistory(resp.data); }
    catch (err) { toast.error("Eroare"); }
    finally { setLoading(false); }
  };

  const fetchPassages = async () => {
    setLoading(true);
    try { const resp = await api.get(`/service-passages?status=${passageFilter}`); setPassages(resp.data); }
    catch (err) { toast.error("Eroare"); }
    finally { setLoading(false); }
  };

  const quickStatusChange = async (scooterId, newStatus) => {
    try {
      const resp = await api.put(`/scooters/${scooterId}/quick-status?status=${newStatus}`);
      toast.success(resp.data.message);
      setScooterStatuses(prev => ({ ...prev, [scooterId]: newStatus }));
      setSearchResults(prev => prev.map(s => s.scooter_id === scooterId ? { ...s, status: newStatus } : s));
    } catch (err) { toast.error("Eroare"); }
  };

  const markVerified = async (id) => {
    try { await api.put(`/programari/${id}`, { status: "verificat" }); toast.success("Verificat!"); fetchProgramari(); }
    catch (err) { toast.error("Eroare"); }
  };

  const updatePassageStatus = async (id, status) => {
    try { await api.put(`/service-passages/${id}?status=${status}`); toast.success(SERVICE_STATUS[status]?.label || status); fetchPassages(); }
    catch (err) { toast.error("Eroare"); }
  };

  // Search scooters
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const resp = await api.get(`/scooters?limit=500`);
      const q = searchQuery.toLowerCase();
      const filtered = (resp.data || []).filter(s =>
        (s.brand || "").toLowerCase().includes(q) ||
        (s.model || "").toLowerCase().includes(q) ||
        (s.scooter_id || "").toLowerCase().includes(q) ||
        (s.vin || "").toLowerCase().includes(q) ||
        (s.color || "").toLowerCase().includes(q)
      ).slice(0, 20);
      setSearchResults(filtered);
      if (filtered.length === 0) toast.info("Niciun scuter gasit");
    } catch (err) { toast.error("Eroare la cautare"); }
    finally { setSearching(false); }
  };

  const sorted = [...programari].sort((a, b) => (getDaysUntil(a.scheduled_date) ?? 999) - (getDaysUntil(b.scheduled_date) ?? 999));
  const todayCount = programari.filter(p => getDaysUntil(p.scheduled_date) === 0).length;
  const urgentCount = programari.filter(p => { const d = getDaysUntil(p.scheduled_date); return d !== null && d >= 0 && d <= 3; }).length;
  const waitingPassages = passages.filter(p => p.status === "in_asteptare" || p.status === "in_lucru").length;

  return (
    <div className="min-h-screen bg-background" data-testid="mechanic-dashboard">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Mecanic</h1>
              <p className="text-xs text-muted-foreground">{user?.username}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="mechanic-logout">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* 4 Tabs */}
        <div className="flex gap-1 bg-card rounded-lg p-1 border border-border overflow-x-auto">
          {[
            { key: "programari", label: "Programari", icon: Calendar },
            { key: "search", label: "Cauta", icon: Search },
            { key: "service", label: "Service", icon: Wrench, badge: waitingPassages || null },
            { key: "istoric", label: "Istoric", icon: History, badge: history.length || null },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 px-2 rounded-md text-xs font-bold transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`} data-testid={`tab-${tab.key}`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.badge > 0 && <span className="bg-red-500 text-white text-[9px] font-mono rounded-full w-4 h-4 flex items-center justify-center">{tab.badge}</span>}
            </button>
          ))}
        </div>

        {/* ===== TAB: PROGRAMARI ===== */}
        {activeTab === "programari" && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Card className="border-border"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono">{programari.length}</p><p className="text-[10px] text-muted-foreground">Total</p></CardContent></Card>
              <Card className="border-red-500/30"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-red-500">{todayCount}</p><p className="text-[10px] text-muted-foreground">Astazi</p></CardContent></Card>
              <Card className="border-yellow-500/30"><CardContent className="p-3 text-center"><p className="text-2xl font-bold font-mono text-yellow-500">{urgentCount}</p><p className="text-[10px] text-muted-foreground">Urgente</p></CardContent></Card>
            </div>
            <div className="flex gap-1 bg-secondary/50 rounded-lg p-1">
              {[{ key: "programat", label: "De facut" }, { key: "verificat", label: "Verificate" }, { key: "all", label: "Toate" }].map(tab => (
                <button key={tab.key} onClick={() => setFilter(tab.key)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${filter === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  data-testid={`filter-${tab.key}`}>{tab.label}</button>
              ))}
            </div>
            {loading ? <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div> :
            sorted.length === 0 ? <div className="text-center py-12 text-muted-foreground"><CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Nicio programare</p></div> :
            <div className="space-y-3">
              {sorted.map(item => {
                const cfg = TYPE_CONFIG[item.programare_type] || TYPE_CONFIG.revizie;
                const Icon = cfg.icon;
                const days = getDaysUntil(item.scheduled_date);
                const isToday = days === 0;
                return (
                  <Card key={item.programare_id} className={`border-border overflow-hidden ${isToday ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`} data-testid={`programare-card-${item.programare_id}`}>
                    <CardContent className="p-0">
                      {isToday && <div className="bg-red-500 text-white text-xs font-bold text-center py-1 flex items-center justify-center gap-1"><AlertTriangle className="w-3 h-3" /> ASTAZI - {item.scheduled_time || "?"}</div>}
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-12 h-12 rounded-xl ${cfg.color} flex items-center justify-center flex-shrink-0`}><Icon className="w-6 h-6 text-white" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1"><span className={`font-bold ${cfg.textColor}`}>{cfg.label}</span>
                              {days !== null && days > 0 && days <= 3 && <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">In {days}z</Badge>}
                            </div>
                            <p className="font-medium text-sm">{item.scooter_label || item.scooter_id}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{item.scheduled_date}</span>
                              {item.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.scheduled_time}</span>}
                              {item.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>}
                            </div>
                            {item.client_name && <p className="text-xs text-muted-foreground mt-1">Client: {item.client_name} {item.client_phone}</p>}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Status scuter</p>
                          <div className="flex gap-1.5 flex-wrap">
                            {SCOOTER_STATUS.map(st => {
                              const StIcon = st.icon;
                              const isActive = scooterStatuses[item.scooter_id] === st.value;
                              return (<button key={st.value} onClick={() => quickStatusChange(item.scooter_id, st.value)}
                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${isActive ? st.active + " border-transparent" : st.color}`}
                                data-testid={`quick-status-${st.value}-${item.scooter_id}`}><StIcon className="w-3 h-3" />{st.label}</button>);
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <Button className="flex-1" onClick={() => navigate(`/mechanic-fiche/${item.scooter_id}?programare=${item.programare_id}`)} data-testid={`open-fiche-${item.programare_id}`}>
                            <FileText className="w-4 h-4 mr-2" /> Deschide Fisa Tehnica <ChevronRight className="w-4 h-4 ml-auto" />
                          </Button>
                          {item.status === "programat" && <Button variant="outline" size="sm" onClick={() => markVerified(item.programare_id)}><CheckCircle className="w-4 h-4" /></Button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>}
          </>
        )}

        {/* ===== TAB: SEARCH SCOOTER ===== */}
        {activeTab === "search" && (
          <div className="space-y-4" data-testid="search-section">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  placeholder="Cauta: brand, model, ID, VIN, culoare..."
                  className="pl-9" data-testid="scooter-search-input" />
              </div>
              <Button onClick={handleSearch} disabled={searching} data-testid="scooter-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">{searchResults.length} rezultate</p>
                {searchResults.map(s => (
                  <Card key={s.scooter_id} className="border-border" data-testid={`search-result-${s.scooter_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-14 h-14 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                          {s.photos?.[0] ? <img src={s.photos[0]} alt="" className="w-full h-full object-cover" /> :
                            <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-muted-foreground" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm">{s.brand} {s.model}</p>
                          <p className="text-xs text-muted-foreground">{s.year} · {s.engine_cc}cc · {s.color} · {s.mileage?.toLocaleString()} km</p>
                          <p className="text-xs font-mono text-muted-foreground">{s.scooter_id}</p>
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {SCOOTER_STATUS.map(st => {
                          const StIcon = st.icon;
                          const isActive = s.status === st.value;
                          return (<button key={st.value} onClick={() => quickStatusChange(s.scooter_id, st.value)}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${isActive ? st.active + " border-transparent" : st.color}`}
                            data-testid={`search-status-${st.value}-${s.scooter_id}`}><StIcon className="w-3 h-3" />{st.label}</button>);
                        })}
                      </div>
                      <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => navigate(`/mechanic-fiche/${s.scooter_id}`)}>
                        <FileText className="w-4 h-4 mr-2" /> Deschide Fisa Tehnica
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {searchResults.length === 0 && !searching && (
              <div className="text-center py-10 text-muted-foreground">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Cauta un scuter dupa brand, model, ID sau VIN</p>
              </div>
            )}
          </div>
        )}

        {/* ===== TAB: SERVICE CLIENT PASSAGE ===== */}
        {activeTab === "service" && (
          <div className="space-y-4" data-testid="service-section">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-sm">Service Client Passage</h2>
              <NewServiceDialog onCreated={fetchPassages} />
            </div>

            <div className="flex gap-1 bg-secondary/50 rounded-lg p-1">
              {[{ key: "in_asteptare", label: "In asteptare" }, { key: "in_lucru", label: "In lucru" }, { key: "all", label: "Toate" }].map(tab => (
                <button key={tab.key} onClick={() => setPassageFilter(tab.key)}
                  className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-colors ${passageFilter === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> :
            passages.length === 0 ? <div className="text-center py-10 text-muted-foreground"><Wrench className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">Niciun service inregistrat</p></div> :
            <div className="space-y-3">
              {passages.map(p => {
                const sType = SERVICE_TYPES.find(t => t.value === p.service_type) || SERVICE_TYPES[0];
                const stCfg = SERVICE_STATUS[p.status] || SERVICE_STATUS.in_asteptare;
                const SIcon = sType.icon;
                return (
                  <Card key={p.passage_id} className="border-border" data-testid={`passage-${p.passage_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <SIcon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-bold text-sm">{sType.label}</span>
                            <Badge className={stCfg.color}>{stCfg.label}</Badge>
                            {p.urgent && <Badge className="bg-red-500 text-white text-[9px]">URGENT</Badge>}
                          </div>
                          <p className="text-sm flex items-center gap-1"><User className="w-3 h-3" /> {p.client_name} {p.client_phone && <span className="text-muted-foreground">· {p.client_phone}</span>}</p>
                          {(p.scooter_brand || p.scooter_model) && <p className="text-xs text-muted-foreground mt-0.5">{p.scooter_brand} {p.scooter_model} {p.scooter_id && `(${p.scooter_id})`}</p>}
                          {p.description && <p className="text-xs text-muted-foreground mt-1 italic">{p.description}</p>}
                          {p.estimated_price > 0 && <p className="text-xs font-mono mt-1">~{p.estimated_price} EUR</p>}
                          <p className="text-[10px] text-muted-foreground mt-1">{new Date(p.created_at).toLocaleString("ro-RO")} · {p.created_by}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {p.status === "in_asteptare" && (
                          <Button size="sm" variant="outline" className="border-blue-500 text-blue-400" onClick={() => updatePassageStatus(p.passage_id, "in_lucru")}>
                            <Wrench className="w-3 h-3 mr-1" /> Incepe
                          </Button>
                        )}
                        {(p.status === "in_asteptare" || p.status === "in_lucru") && (
                          <Button size="sm" variant="outline" className="border-green-500 text-green-400" onClick={() => updatePassageStatus(p.passage_id, "finalizat")}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Finalizat
                          </Button>
                        )}
                        {p.status !== "anulat" && p.status !== "finalizat" && (
                          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => updatePassageStatus(p.passage_id, "anulat")}>Anuleaza</Button>
                        )}
                        {p.scooter_id && (
                          <Button size="sm" variant="outline" onClick={() => navigate(`/mechanic-fiche/${p.scooter_id}`)}>
                            <FileText className="w-3 h-3 mr-1" /> Fisa
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>}
          </div>
        )}

        {/* ===== TAB: ISTORIC ===== */}
        {activeTab === "istoric" && (
          <div className="space-y-4" data-testid="istoric-section">
            {loading ? <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div> :
            history.length === 0 ? <div className="text-center py-12 text-muted-foreground"><History className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Niciun istoric</p></div> :
            <>
              <p className="text-xs text-muted-foreground">{history.length} inregistrari</p>
              {history.map((entry, idx) => {
                const date = entry.timestamp ? new Date(entry.timestamp) : null;
                const dateStr = date ? date.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "";
                const timeStr = date ? date.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" }) : "";
                const prevDate = idx > 0 && history[idx-1].timestamp ? new Date(history[idx-1].timestamp).toLocaleDateString("ro-RO") : "";
                const showDateHeader = idx === 0 || dateStr !== prevDate;
                return (
                  <div key={idx}>
                    {showDateHeader && <div className="flex items-center gap-2 pt-2 pb-1"><div className="h-px flex-1 bg-border" /><span className="text-[10px] text-muted-foreground font-mono uppercase">{dateStr}</span><div className="h-px flex-1 bg-border" /></div>}
                    <Card className="border-border"><CardContent className="p-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5"><Wrench className="w-4 h-4 text-primary" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-sm truncate">{entry.scooter_label || entry.scooter_id}</p>
                            {entry.score > 0 && <Badge className={`text-[10px] ${entry.score >= 4 ? 'bg-green-500' : entry.score >= 3 ? 'bg-yellow-500' : 'bg-red-500'} text-white`}>{entry.score}/5</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{timeStr} · {entry.user || "mecanic"}{entry.details && ` · ${entry.details}`}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => navigate(`/mechanic-fiche/${entry.scooter_id}`)}><ChevronRight className="w-4 h-4" /></Button>
                      </div>
                    </CardContent></Card>
                  </div>
                );
              })}
            </>}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== New Service Dialog =====
function NewServiceDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_phone: "", scooter_brand: "", scooter_model: "", scooter_id: "", service_type: "reparatie", description: "", estimated_price: 0, urgent: false });

  const handleSubmit = async () => {
    if (!form.client_name) { toast.error("Numele clientului e obligatoriu"); return; }
    setLoading(true);
    try {
      await api.post("/service-passages", form);
      toast.success("Service inregistrat!");
      setOpen(false);
      setForm({ client_name: "", client_phone: "", scooter_brand: "", scooter_model: "", scooter_id: "", service_type: "reparatie", description: "", estimated_price: 0, urgent: false });
      onCreated();
    } catch (err) { toast.error("Eroare"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="new-service-btn"><Plus className="w-4 h-4 mr-1" /> Nou</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Service Client Passage</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nume client *</Label><Input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Ion Popescu" data-testid="svc-client-name" /></div>
            <div><Label className="text-xs">Telefon</Label><Input value={form.client_phone} onChange={e => setForm(p => ({ ...p, client_phone: e.target.value }))} placeholder="+40721..." /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Brand scuter</Label><Input value={form.scooter_brand} onChange={e => setForm(p => ({ ...p, scooter_brand: e.target.value }))} placeholder="Kymco" /></div>
            <div><Label className="text-xs">Model</Label><Input value={form.scooter_model} onChange={e => setForm(p => ({ ...p, scooter_model: e.target.value }))} placeholder="Agility" /></div>
            <div><Label className="text-xs">ID scuter</Label><Input value={form.scooter_id} onChange={e => setForm(p => ({ ...p, scooter_id: e.target.value }))} placeholder="SC..." /></div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Tip service</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {SERVICE_TYPES.map(st => (
                <button key={st.value} onClick={() => setForm(p => ({ ...p, service_type: st.value }))}
                  className={`flex items-center gap-1 p-2 rounded-lg border text-xs transition-all ${form.service_type === st.value ? 'border-primary bg-primary/10 font-bold' : 'border-border hover:border-primary/50'}`}
                  data-testid={`svc-type-${st.value}`}>
                  <st.icon className="w-3 h-3" /> {st.label}
                </button>
              ))}
            </div>
          </div>
          <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Detalii reparatie..." /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Pret estimat (EUR)</Label><Input type="number" value={form.estimated_price} onChange={e => setForm(p => ({ ...p, estimated_price: parseFloat(e.target.value) || 0 }))} /></div>
            <div className="flex items-end">
              <button onClick={() => setForm(p => ({ ...p, urgent: !p.urgent }))}
                className={`w-full py-2 rounded-lg border text-sm font-medium transition-all ${form.urgent ? 'bg-red-500 text-white border-transparent' : 'border-border text-muted-foreground hover:border-red-500/50'}`}>
                {form.urgent ? "URGENT" : "Normal"}
              </button>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full" data-testid="submit-service">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Inregistreaza
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
